<template>
  <GridChartCard
    :title="title"
    :subtitle="subtitle"
    ref="chartCard"
    :hasData="data.data.length > 0"
    :disableCsv="true"
    @draw="createChart"
    @download-csv="data.generateCSV(selectedDestName, csvOptions.includeSummary)"
    :tooltip="tooltip"
    :noDataText="noDataText"
  >
    <template #actions>
      <Icon
      v-if="isInteractive && getIcon && showSecondaryGrouping"
      iconColor="white"
      :tooltipColor="contractMode.details.color"
      :tooltipText="secondaryGroupingTooltip"
      :small="false"
      margin="mr-3"
      :disabled="data.data.length === 0 || data.dataSets.sets.length <= 1"
      @icon-clicked="secondaryGrouping = !secondaryGrouping"
      :icon="getIcon"
      elementId="toggle-secondary-grouping"/>

      <Icon
      iconColor="white"
      :tooltipText="$t('refresh')"
      margin="mr-3"
      @icon-clicked="refresh"
      icon="mdi-refresh"
      :small="false"/>
    </template>
  </GridChartCard>
</template>

<script>
import { Chart } from 'chart.js'
import { mapActions, mapGetters } from 'vuex'
import { getChartColors, getChartColor } from './ChartColors'
import IconHelpers from '@/utils/IconHelpers'
import { ContractMode } from '@/utils/Enumerations'
import { chartTooltipStyle } from '@/utils/ChartHelpers'

export default {
  name: 'StackedBarChartCardForByproductLoadsByDestination',

  components: {
    GridChartCard: () => import('./GridChartCard.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  data: () => ({
    IconHelpers,
    chart: null,
    viewingTons: false,
    secondaryGrouping: true
  }),

  props: {
    data: {
      type: Object,
      required: true
    },
    elementId: {
      type: String,
      required: true
    },
    selectedDestName: {
      type: String,
      required: false,
      defualt: ''
    },
    selectedDestLocationId: {
      type: Number,
      required: false,
      default: -1
    },
    contractMode: {
      type: Object,
      default: () => ContractMode.Logs
    },
    overrideTitle: {
      type: String,
      default: ''
    },
    csvOptions: {
      type: Object,
      default: () => ({
        includeSummary: false
      })
    },
    type: String,
    customTitle: {
      type: Function,
      required: false,
      default: undefined
    },
    tooltip: {
      type: String,
      required: false,
      default: undefined
    },
    loadStatus: {
      type: String,
      required: false,
      default: undefined
    },
    allowToggle: {
      type: Boolean,
      required: false,
      default: true
    },
    loadsOrTonsInTitle: {
      type: Boolean,
      required: false,
      default: true
    },
    allowSecondaryGrouping: {
      type: Boolean,
      default: true
    },
    isInteractive: {
      type: Boolean,
      default: false
    },
    noDataText: {
      type: String,
      default: undefined
    }
  },

  created () {
    this.secondaryGrouping = this.allowSecondaryGrouping
  },

  watch: {
    data: {
      handler (_) {
        this.scheduleDraw()
      },
      deep: true
    },

    viewingTons () {
      this.scheduleDraw()
    },

    secondaryGrouping () {
      this.scheduleDraw()
    }
  },

  computed: {
    ...mapGetters('dashboard', ['interactiveChartTooltipFooter']),
    title () {
      const unit = this.viewingTons ? this.$t('tons') : this.$t('loads')
      let title = (this.overrideTitle || this.customTitle?.call(null, unit)) ?? `${this.loadStatus ? this.loadStatus + ' ' : ''}${unit} by ${this.type}`
      const basedOn = this.viewingTons ? this.$t('weight') : this.$t('count')
      if (!this.loadsOrTonsInTitle) {
        title = title + ' ' + this.$t('percentageBy', { basedOn })
      }

      return (this.selectedDestName && this.$vuetify.breakpoint.lgAndUp)
        ? `${title} (${this.selectedDestName})`
        : title
    },

    subtitle () {
      if (this.$vuetify.breakpoint.mdAndDown && this.selectedDestName) {
        return this.selectedDestName
      }
      return undefined
    },

    headerStyle () {
      return 'background-color: #363636; color: white;'
    },

    weightUnitToggleToolTip () {
      return this.$t('tons')
    },

    countUnitToggleToolTip () {
      return this.$t('loadCount')
    },

    disabledCsv () {
      return !this.csvOptions
    },

    secondaryGroupingTooltip () {
      return this.$t(this.secondaryGrouping
        ? 'disableGroupingByCategory'
        : 'groupByCategory',
      {
        category: this.$t(this.data.data[0]?.loadCountAndFromAccount
          ? 'fromAccount' : this.data.miniBarType)
      })
    },

    getIcon () {
      if (this.data.miniBarType) {
        return IconHelpers.getIconForObjectType(this.data.miniBarType, this.secondaryGrouping)
      } else if (this.data.data[0]?.loadCountAndFromAccount) {
        return IconHelpers.getIconForObjectType('fromAccount', this.secondaryGrouping)
      } else {
        return undefined
      }
    },

    viewTicketsStyle () {
      return {
        position: 'absolute'
      }
    },

    yAxisTitle () {
      return undefined
    },

    dataVersion () {
      return 'data'
    },

    dataSetsVersion () {
      return 'dataSets'
    },

    showSecondaryGrouping () {
      return this.data.miniBarType !== 'destination' || this.selectedDestLocationId === -1
    }
  },

  methods: {
    ...mapActions('dashboard', ['getNestedCSV', 'getWidgetInteractionStatus']),

    scheduleDraw () {
      this.$nextTick(() => {
        if (this.$refs.chartCard) this.$refs.chartCard.draw()
      })
    },

    percentOfTotal (value, loads, tractIndex) {
      const loadArray = loads.map(product => (this.viewingTons) ? product.tonData[tractIndex] : product.loadData[tractIndex])
      const total = loadArray.reduce((a, b) => a + b, 0)
      return (value / total * 100).toFixed(1)
    },

    async onDataClicked (e) {
      if (!this.isInteractive) {
        return
      }
      const element = this.chart.getElementsAtEventForMode(e, 'point', true)
      const data = this.chart.config._config.data
      const xAxisValue = data.labels[element[0]?.index]
      const yAxisValue = data.datasets[element[0]?.datasetIndex]?.label
      if (await this.getWidgetInteractionStatus({ widgetId: this.widgetId, x: xAxisValue, y: yAxisValue })) {
        this.$emit('data-clicked', {
          xAxisValue: xAxisValue,
          yAxisValue: yAxisValue
        })
      }
    },

    createChart (canvas) {
      const percentOfTotal = this.percentOfTotal
      const dataSets = this.data[this.dataSetsVersion]
      const tickFormat = (value) => value
      const yAxisTitle = {
        display: this.yAxisTitle,
        text: this.yAxisTitle
      }
      const chart = new Chart(canvas, {
        type: 'bar',
        data: {
          labels: dataSets.labels,
          datasets: this.secondaryGrouping && this.showSecondaryGrouping ? dataSets.sets.map((set, index) => {
            return {
              label: set.label,
              data: this.viewingTons ? set.tonData : set.loadData,
              backgroundColor: getChartColor(index, dataSets.sets.length, ContractMode.Byproducts.value)
            }
          }) : [{
            data: this.data[this.dataVersion].map(d => this.viewingTons ? d.tons : d.loadCount ?? d.loadCountAndFromAccount.reduce((acc, curr) => acc + curr.loadCount, 0)),
            backgroundColor: getChartColors(dataSets.sets.length, ContractMode.Byproducts.value),
            labels: undefined,
            chartType: this.type
          }]
        },
        options: {
          maintainAspectRatio: false,
          responsive: true,
          resizeDelay: 500,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              callbacks: {
                label: this.secondaryGrouping && this.showSecondaryGrouping ? function (toolTipItem) {
                  const value = toolTipItem.parsed.y
                  if (this.secondaryGrouping && this.showSecondaryGrouping === false) {
                    return `${value}`
                  } else {
                    const label = toolTipItem.dataset.label
                    const percent = percentOfTotal(value, dataSets.sets, toolTipItem.dataIndex)
                    return `${label}: ${value} (${percent}%)`
                  }
                } : function (toolTipItem) {
                  return `${toolTipItem.parsed.y}`
                },
                title: function (titleItem) {
                  const chartType = titleItem[0]?.dataset?.chartType ?? undefined
                  return chartType === 'ItemizedDefectSummaryChart' ? undefined : titleItem[0].label
                }
              },
              ...chartTooltipStyle
            }
          },
          scales: {
            x: {
              stacked: true
            },
            y: {
              stacked: true,
              ticks: {
                precision: 0,
                callback: function (value) {
                  return tickFormat(value)
                }
              },
              title: {
                display: yAxisTitle.display,
                text: yAxisTitle.text
              }
            }
          },
          onClick: (e) => this.onDataClicked(e)
        }
      })
      if (this.isInteractive) chart.options.plugins.tooltip.callbacks.footer = () => this.interactiveChartTooltipFooter
      this.chart = chart
    },

    refresh () {
      this.$emit('refresh')
    }
  }
}
</script>
